/**
 * How a table's rows compare when somebody sorts them.
 *
 * ITS OWN FILE, with no editor imports, for the same reason columnTypes.ts and mentions.ts are:
 * the decisions worth pinning are which order things come out in and what a blank cell does, and
 * those are pure. TableOptions.ts cannot be unit-tested at all -- it pulls in TipTap's ESM -- so
 * anything testable that lives there is in practice untested.
 */

export type SortDirection = "asc" | "desc";

/**
 * Compare two cells the way a reader expects.
 *
 * NUMBERS AS NUMBERS. A column of "2, 10, 1" sorts 1, 2, 10 rather than 1, 10, 2 -- the mistake
 * every naive sort makes, and an obvious one in a procedure with numbered steps. Thousands
 * separators are tolerated because people paste from spreadsheets.
 *
 * ISO DATES NEED NO SPECIAL CASE: 2026-10-06 sorts chronologically as text, which is one of the
 * reasons the column-type rule accepts that form and refuses slashed dates.
 *
 * AN EMPTY CELL SINKS, whichever direction is chosen. A blank is an absence, not a smallest value,
 * and a sort that floated every unfilled row to the top would bury the data somebody wanted to see.
 */
export function compareCells(left: string, right: string): number {
    if (!left && !right) return 0;
    if (!left) return 1;
    if (!right) return -1;

    const asNumber = (text: string) => {
        const cleaned = text.replace(/,/g, "");
        return /^[+-]?\d+(\.\d+)?$/.test(cleaned) ? parseFloat(cleaned) : null;
    };
    const leftNumber = asNumber(left);
    const rightNumber = asNumber(right);
    if (leftNumber !== null && rightNumber !== null) return leftNumber - rightNumber;

    return left.localeCompare(right, undefined, { sensitivity: "base", numeric: true });
}

/**
 * `values` in sorted order, carrying whatever each one is attached to.
 *
 * STABLE, and that matters here more than it usually does: rows that compare equal keep the order
 * the author put them in, and in a procedure the original order is often meaningful.
 */
export function sortedByKey<T>(items: T[], key: (item: T) => string,
                               direction: SortDirection): T[] {
    const decorated = items.map((item, at) => ({ item, at, value: key(item) }));

    // THE BLANKS COME OUT FIRST AND GO BACK ON THE END, outside the direction flip. Sinking them
    // inside the comparison does not work: the flip that reverses the order reverses the sink with
    // it, and descending floated every unfilled row to the top. Found by the test, not by thinking
    // about it -- which is why the test asserts BOTH directions rather than one.
    const filled = decorated.filter(one => one.value !== "");
    const blank = decorated.filter(one => one.value === "");

    filled.sort((a, b) => {
        const by = compareCells(a.value, b.value);
        if (by !== 0) return direction === "asc" ? by : -by;
        return a.at - b.at;
    });

    return [...filled, ...blank].map(one => one.item);
}
